import { cn } from '../../utils/cn';

const toneActive = {
  brand:   'border-brand-500 bg-brand-50/60 text-brand-700',
  neutral: 'border-status-neutral bg-status-neutral-soft text-fg',
  warning: 'border-status-warning bg-status-warning-soft text-status-warning',
  success: 'border-status-success bg-status-success-soft text-status-success',
  error:   'border-status-error bg-status-error-soft text-status-error',
  info:    'border-status-info bg-status-info-soft text-status-info',
};

const toneIcon = {
  brand:   'text-brand-700',
  neutral: 'text-fg-muted',
  warning: 'text-status-warning',
  success: 'text-status-success',
  error:   'text-status-error',
  info:    'text-status-info',
};

const toneBar = {
  brand:   'bg-brand-500',
  neutral: 'bg-status-neutral',
  warning: 'bg-status-warning',
  success: 'bg-status-success',
  error:   'bg-status-error',
  info:    'bg-status-info',
};

/**
 * KpiFilterCards — grilla de tarjetas KPI que actúan como filtro de estado.
 *
 * Primitivo canónico del DS (promovido desde
 * src/components/recolecciones/KpiFilterCards.jsx). Cada tarjeta es un botón
 * accesible (aria-pressed) que reporta su `value` vía `onChange`. La tarjeta
 * con `value === ''` representa el total y no dibuja barra de progreso.
 *
 * Un conteo (o el total) en `null` significa "desconocido": se dibuja '—' y sin barra, en vez
 * de un 0 que se leería como dato. Un conteo ausente (undefined) sigue siendo 0.
 *
 * @param {{
 *   cards: Array<{ value: string, label: string, icon?: React.ComponentType<{className?: string}>, tone?: 'brand'|'neutral'|'warning'|'success'|'error'|'info' }>,
 *   counts: Record<string, number|null>,
 *   total: number|null,
 *   active: string,
 *   onChange: (value: string) => void,
 *   className?: string,
 * }} props
 */
export function KpiFilterCards({ cards, counts, total, active, onChange, className }) {
  return (
    <div className={cn('grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3', className)}>
      {cards.map((card) => {
        const isActive = active === card.value;
        const raw = card.value === '' ? total : counts[card.value];
        const count = raw === null ? null : (raw ?? 0);
        const Icon = card.icon;
        const pct =
          count !== null && total !== null && total > 0 && card.value !== ''
            ? (count / total) * 100
            : null;

        return (
          <button
            key={card.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(card.value)}
            className={cn(
              'text-left rounded-xl border-2 px-4 py-3 transition-all',
              'hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              isActive
                ? (toneActive[card.tone] ?? toneActive.neutral)
                : 'border-border bg-bg text-fg hover:border-brand-300',
            )}
          >
            <div className="flex items-center justify-between mb-2">
              {Icon && (
                <Icon
                  className={cn(
                    'w-4 h-4',
                    isActive ? (toneIcon[card.tone] ?? 'text-fg') : 'text-fg-muted',
                  )}
                />
              )}
              <span
                className={cn(
                  'text-2xl font-semibold tabular-nums',
                  isActive ? '' : 'text-fg',
                )}
              >
                {count === null ? '—' : count}
              </span>
            </div>
            <div className="text-xs font-medium text-fg-muted uppercase tracking-wide">{card.label}</div>
            {pct !== null && (
              <div className="mt-2 h-1 rounded-full bg-bg-subtle overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    toneBar[card.tone] ?? 'bg-brand-500',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default KpiFilterCards;
